import React, {
	PropsWithChildren,
	useCallback,
	useLayoutEffect,
	useRef,
} from "react";

import gsap from "gsap";

import { cn } from "@/utils/cn";

export interface IStickyHeaderProps extends PropsWithChildren {
	scroller?: string | HTMLElement;
	className?: string;
	scrollTrigger?: ScrollTriggerConfig;
}

interface ScrollTriggerConfig {
	target?: string;
	start?: string;
	end?: string;
	paddingTop?: number;
}

const DEFAULT_SCROLL_TRIGGER: ScrollTriggerConfig = {
	target: ".task-list-header",
	start: "top+=0",
	end: "+=36",
	paddingTop: 0,
};

export const StickyHeader: React.FC<IStickyHeaderProps> = ({
	className,
	scroller,
	scrollTrigger = DEFAULT_SCROLL_TRIGGER,
	children,
}) => {
	const headerContRef = useRef<HTMLDivElement>(null);
	const headerRef = React.useRef<HTMLDivElement>(null);

	const timeoutRef = useRef<
		[NodeJS.Timeout | null, NodeJS.Timeout | null, NodeJS.Timeout | null]
	>([null, null, null]);

	const tlRef = useRef<gsap.core.Timeline | null>(null);

	const handleResize = useCallback(() => {
		const headerContainer = headerContRef.current;

		if (!headerContainer || !headerRef.current) return;

		timeoutRef.current.forEach((id) => {
			if (id) clearTimeout(id);
		});
		timeoutRef.current[0] = setTimeout(() => {
			headerContainer.style.position = "relative";
			headerContainer.style.bottom = "unset";

			timeoutRef.current[1] = setTimeout(() => {
				tlRef.current?.scrollTrigger?.refresh();
				timeoutRef.current[2] = setTimeout(() => {
					headerContainer.style.position = "sticky";
					headerContainer.style.bottom = "0px";
				}, 50);
			}, 50);
		}, 500);
	}, []);

	useLayoutEffect(() => {
		const headerContainer = headerContRef.current;

		if (!headerContainer || !headerRef.current) return;

		tlRef.current?.scrollTrigger?.kill();
		tlRef.current?.kill();

		const triggerEl = scrollTrigger?.target
			? document.querySelector(scrollTrigger.target)
			: headerContRef.current;

		tlRef.current = gsap.timeline({
			scrollTrigger: {
				trigger: triggerEl,
				start: scrollTrigger?.start ?? "top top",
				end: scrollTrigger?.end ?? "+=36",
				...(scroller ? { scroller } : {}),
				invalidateOnRefresh: true,
				onUpdate: ({ progress }) => {
					if (!headerRef.current || !headerContRef.current) return;
					headerRef.current.style.top = `${progress * 1}rem`;
					if (scrollTrigger.paddingTop)
						headerContRef.current.style.paddingTop = `${scrollTrigger.paddingTop * progress}px`;
				},
				...scrollTrigger,
			},
		});

		window.addEventListener("resize", handleResize);

		// The same array handleResize fills in place: its latest ids at cleanup.
		const resizeTimeouts = timeoutRef.current;
		const settle = setTimeout(() => {
			headerContainer.style.bottom = "0px";
		}, 50);

		return () => {
			window.removeEventListener("resize", handleResize);
			// A resize still settling must not restyle a header that is gone.
			clearTimeout(settle);
			resizeTimeouts.forEach((id) => {
				if (id) clearTimeout(id);
			});
			tlRef.current?.scrollTrigger?.kill();
			tlRef.current?.kill();
		};
	}, [scroller, scrollTrigger, handleResize]);

	return (
		<div
			className={cn([
				"overflow-x-hiddden sticky top-[-2px] overflow-y-visible bg-linear-to-b from-white from-50% via-transparent via-50% to-transparent to-50%",
				className,
			])}
			ref={headerContRef}
		>
			<div
				ref={headerRef}
				className="relative flex flex-col justify-between overflow-x-hidden rounded-xl bg-white px-2 py-2 md:px-4"
			>
				{children}
			</div>
		</div>
	);
};
